import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { modelUtils } from '@pelicargo/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { FieldArrayWithId, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { HiArchiveBoxArrowDown, HiChevronDown, HiPlus } from 'react-icons/hi2';

import { SimpleForm } from '../components/SimpleForm';
import { trpc } from '../config/trpc';
import { API } from '../utils/typings';
import { SegmentEditorItemFormRow } from './SegmentEditorItemFormRow';

export type LegWithFlightSegments = API.GetFlightSegmentsByRouteIds[0] & { _id?: string };
export type SegmentsFormValues = { legs: LegWithFlightSegments[] };

type Props = {
  routeIds: number[];
  providerId: string;
};

export const RouteEditorSegments = ({ providerId, routeIds }: Props) => {
  const { refetch } = trpc.getFlightSegmentsByRouteIds.useQuery(
    { routeIds, providerId },
    { enabled: !!routeIds && !!providerId },
  );
  const { mutateAsync: bulkModifyFlightSegments, isLoading: isUpdatingSegments } =
    trpc.bulkModifyFlightSegments.useMutation();

  const segmentsForm = useForm<SegmentsFormValues>({ defaultValues: { legs: [] } });
  const { handleSubmit, formState, reset } = segmentsForm;

  const handleResetForm = useCallback(async () => {
    // reset the form values to blank to ensure we don't have RHF try to smart-hydrate data when we have new similar data
    // as indices get a bit weird when we just reset with new data
    reset({ legs: [] });

    // some internal react features makes a flushSync above not necessary as react does not batch updates once it hits a promise
    // we can clear the form entirely above, and then refetch the data to ensure we have the latest data
    const { data } = await refetch();
    reset({ legs: modelUtils.flightSegments.sortByAirport(data) });
  }, [reset, refetch]);

  useEffect(() => {
    handleResetForm();

    // intentional form reset to ensure the form is always up-to-date with the latest data when routeIds are changed
    // this occurs when we change our selection of the routes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeIds]);

  const onValid = useCallback(
    ({ preserveHistory }: { preserveHistory: boolean }) =>
      async (values: SegmentsFormValues) => {
        const dirtyFields = formState.dirtyFields;

        const dirtySegments = Object.keys(dirtyFields.legs || {}).flatMap((legIndex) => {
          const legDirtyFields = dirtyFields.legs[legIndex]?.segments || {};
          return Object.keys(legDirtyFields)
            .filter((segmentIndex) => Object.values(legDirtyFields[segmentIndex]).some((value) => value === true))
            .map((segmentIndex) => values.legs[legIndex].segments[segmentIndex]);
        });

        await bulkModifyFlightSegments({ flight_segments: dirtySegments, preserve_history: preserveHistory });
        handleResetForm();
      },
    [formState.dirtyFields, bulkModifyFlightSegments, handleResetForm],
  );

  const dirtyFieldCount = Object.entries(formState?.dirtyFields?.legs || {}).reduce((count, [, legDirtyFields]) => {
    const segmentsDirtyFields = legDirtyFields?.segments || {};
    return (
      count +
      Object.keys(segmentsDirtyFields).filter((segmentIndex) =>
        Object.values(segmentsDirtyFields[segmentIndex]).some((value) => value === true),
      ).length
    );
  }, 0);

  return (
    <Stack w="full" direction="column" p={6} spacing={6}>
      <SimpleForm showToast={false} {...(segmentsForm as any)} ignoreOnBlur submitOnBlur={false}>
        <Stack direction="row" spacing="4" justify="space-between" alignItems="flex-start">
          <Heading variant="h5">Segments</Heading>
          <Stack direction="row" alignItems="center" spacing="4">
            <Show if={dirtyFieldCount > 0}>
              <Text fontSize="sm" color="gray.500">
                {dirtyFieldCount} unsaved segments
              </Text>
            </Show>
            <Button onClick={handleResetForm} colorScheme="secondary" variant="ghost">
              Reset
            </Button>
            <ButtonGroup isAttached>
              <Button
                isLoading={isUpdatingSegments}
                onClick={handleSubmit(onValid({ preserveHistory: true }))}
                colorScheme="primary"
              >
                Save & Keep History
              </Button>
              <Menu>
                <MenuButton as={IconButton} colorScheme="primary" icon={<HiChevronDown />} />
                <MenuList>
                  <MenuItem
                    icon={<HiArchiveBoxArrowDown />}
                    onClick={handleSubmit(onValid({ preserveHistory: false }))}
                  >
                    Save & Discard History
                  </MenuItem>
                </MenuList>
              </Menu>
            </ButtonGroup>
          </Stack>
        </Stack>
        <RouteEditorLegForm />
      </SimpleForm>
    </Stack>
  );
};

export const RouteEditorLegForm = () => {
  const { control } = useFormContext<SegmentsFormValues>();
  const { fields: legs } = useFieldArray<SegmentsFormValues, 'legs', '_id'>({ keyName: '_id', control, name: 'legs' });

  const renderLeg = useCallback((leg: FieldArrayWithId<SegmentsFormValues, 'legs', '_id'>, index: number) => {
    return <RouteEditorLegCard key={leg.path} leg={leg} prefix={`legs.${index}`} />;
  }, []);

  return <Stack spacing="4">{legs?.map(renderLeg)}</Stack>;
};

export const RouteEditorLegCard = ({ leg, prefix }: { leg: LegWithFlightSegments; prefix: `legs.${number}` }) => {
  const { control } = useFormContext<SegmentsFormValues, `legs.${number}`>();
  const {
    fields: segments,
    append: appendFlightSegment,
    remove: removeFromForm,
  } = useFieldArray<SegmentsFormValues, `legs.${number}.segments`, '_id'>({
    keyName: '_id',
    control,
    name: `${prefix}.segments`,
  });

  const handleAddSegment = () => {
    appendFlightSegment({
      id: null,

      aircraft_type: null,
      max_cm_height: 0,
      max_cm_length: 0,
      max_cm_width: 0,
      max_kg_per_piece: 0,

      provider_id: leg.provider_id,
      origin_airport_id: leg.origin_airport_id,
      destination_airport_id: leg.destination_airport_id,
      flight_path_position: leg?.segments?.[0]?.flight_path_position,
      is_final_destination: leg?.segments?.[0]?.is_final_destination,

      is_schedule: true,
      aircraft_id: null,
      departs_at: null,
      arrives_at: null,
      expires_at: null,
      starts_at: null,
      flight_number: null,
      schedule: [],
      expiry_reason: null,
      // TODO: casting so that it satisfies the "_count" fields etc since we don't want those as required in the client
    } as LegWithFlightSegments['segments'][0]);
  };

  const renderSegment = useCallback(
    (
      segment: FieldArrayWithId<SegmentsFormValues, `legs.${number}.segments`, '_id'>,
      index: number,
      arr: FieldArrayWithId<SegmentsFormValues, `legs.${number}.segments`, '_id'>[],
    ) => {
      const segmentName = `${prefix}.segments.${index}`;
      const isCreated = segment.id > 0;

      const createdSegments = arr?.filter((s) => s.id > 0);

      return (
        <SegmentEditorItemFormRow
          key={segmentName}
          name={segmentName}
          removeFromForm={() => (isCreated ? undefined : removeFromForm(index))}
          isDeleteDisabled={isCreated && createdSegments.length === 1}
        />
      );
    },
    [prefix, removeFromForm],
  );

  const impactedRoutesCopy = useMemo(() => {
    if (leg.numOfRoutes === null) return 'No count for other routes, this is probably a new segment.';

    const count = leg.numOfRoutes ? leg.numOfRoutes - 1 : 0;
    return `Changes will affect ${count} other routes.`;
  }, [leg.numOfRoutes]);

  return (
    <Card overflow="hidden">
      <CardHeader py={2} px={4}>
        <Stack spacing={0} direction="row" justifyContent="space-between" alignItems="center">
          <Text fontWeight="bold">
            {leg.origin_airport_id} {leg.destination_airport_id ? `→ ${leg.destination_airport_id}` : ''}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {impactedRoutesCopy}
          </Text>
        </Stack>
      </CardHeader>
      <Divider />
      <CardBody overflowX="auto" p="0">
        <Table variant="simple" borderRadius="0">
          <Thead>
            <Tr>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" />
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Aircraft
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Aircraft Type
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Max L (CM)
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Max W (CM)
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Max H (CM)
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Max Wght/Pc (KG)
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Starts At
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Expires At
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Departs At
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Arrives At
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Flight #
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1" borderRightWidth={1} p={1}>
                Schedule
              </Td>
              <Td fontSize="xs" fontWeight="bolder" lineHeight="1">
                Expiry
              </Td>
            </Tr>
          </Thead>
          <Tbody>{segments?.map(renderSegment)}</Tbody>
        </Table>
        <Stack w="full">
          <Button
            size="sm"
            variant="ghost"
            colorScheme="primary"
            rightIcon={<HiPlus />}
            onClick={handleAddSegment}
            borderRadius="none"
          >
            Add Segment
          </Button>
        </Stack>
      </CardBody>
    </Card>
  );
};
